import { useState, useEffect } from "react";
import { Archive, ArchiveRestore, Loader, AlertCircle } from "lucide-react";

const MODE_CONFIG = {
  archive: {
    Icon: Archive,
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
    title: "Archive Company?",
    confirmLabel: "Yes, Archive",
    color: "#d97706",
    hoverColor: "#b45309",
  },
  unarchive: {
    Icon: ArchiveRestore,
    iconBg: "bg-sky-50",
    iconColor: "text-[#009DB9]",
    title: "Unarchive Company?",
    confirmLabel: "Yes, Unarchive",
    color: "#009DB9",
    hoverColor: "#007a90",
  },
};

export default function ConfirmArchiveModal({
  open,
  mode = "archive",
  companyName,
  loading = false,
  error = "",
  onClose,
  onConfirm,
}) {
  const [reason, setReason] = useState("");

  // Start with an empty reason every time the modal opens
  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  if (!open) return null;

  const cfg = MODE_CONFIG[mode] || MODE_CONFIG.archive;
  const { Icon } = cfg;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-sm mx-4 overflow-hidden bg-white shadow-2xl rounded-2xl">
        <div className="flex flex-col items-center px-6 pb-4 text-center pt-7">
          <div className={`flex items-center justify-center mb-4 w-14 h-14 rounded-2xl ${cfg.iconBg}`}>
            <Icon size={24} className={cfg.iconColor} strokeWidth={2} />
          </div>

          <h2 className="text-[16px] font-bold text-neutral-900">{cfg.title}</h2>

          {mode === "archive" ? (
            <p className="text-[13px] text-neutral-500 mt-1.5 leading-relaxed">
              You are about to archive{" "}
              <span className="font-semibold text-neutral-800">"{companyName}"</span>.
              <br />
              It will be moved to the archived companies and removed from the active list.
              A Sales Manager can restore it later.
            </p>
          ) : (
            <p className="text-[13px] text-neutral-500 mt-1.5 leading-relaxed">
              You are about to unarchive{" "}
              <span className="font-semibold text-neutral-800">"{companyName}"</span>.
              <br />
              It will be removed from the archived companies and restored to the active list.
            </p>
          )}

          {mode === "archive" && (
            <div className="flex flex-col w-full gap-1 mt-4 text-left">
              <label
                htmlFor="archive-reason"
                className="text-[11px] font-semibold tracking-wide uppercase text-neutral-500"
              >
                Reason <span className="font-normal normal-case text-neutral-400">(optional)</span>
              </label>
              <textarea
                id="archive-reason"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={loading}
                placeholder="Why is this company being archived?"
                className="w-full px-3 py-2 text-[12px] border rounded-lg outline-none resize-none border-neutral-200 focus:border-[#009DB9] disabled:bg-neutral-50"
              />
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="flex items-start w-full gap-2 p-3 mt-4 text-left text-red-600 border border-red-200 rounded-lg bg-red-50"
            >
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              <p className="text-[12px] leading-snug">{error}</p>
            </div>
          )}
        </div>

        <div className="flex gap-2.5 px-6 pb-6 pt-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 h-10 rounded-xl text-[13px] font-semibold border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(mode === "archive" ? reason : undefined)}
            disabled={loading}
            className="flex items-center justify-center flex-1 h-10 gap-2 text-[13px] font-semibold text-white transition-colors rounded-xl disabled:opacity-50"
            style={{ background: cfg.color }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = cfg.hoverColor; }}
            onMouseLeave={(e) => (e.currentTarget.style.background = cfg.color)}
          >
            {loading ? <Loader size={14} className="animate-spin" /> : error ? "Retry" : cfg.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}